import { ConfigurationType } from '@server/parser';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectSingleObject } from '../../../reducers/documentation/documentationReducer';
import { selectParsedFields } from '../../../reducers/global/globalReducer';
import { DocumentationTabTypes } from '../../../types/enums/DocumentationTabTypes';
import { updateStructureWithParentElementType } from './filters/utils';
import { useDocumentation } from '../useDocumentation';
import { ruleByObjectName } from '../../parser/parserUtils';
import groupBy from 'lodash/groupBy';
import {
  createConfigurationItemFromAutomation,
  createConfigurationItemFromField,
  createConfigurationItemFromParsedRule,
  createConfigurationItemFromRecordType,
} from '../helpers';
import { selectRollupsForTable } from '../../../reducers/global/rollupReducers';
import { ConfigurationItem } from '../dependencies/DependenciesTypes';
import { selectAccountUsers } from '../../../reducers/accountUsersReducer';
import { useGetDeployedVersions } from './useGetDeployedVersions';
import { useGetRelatedObjects } from './useGetRelatedObjects';

export const usePrepareActiveObjectStructure = (
  crmOrgId: string,
): { [key in DocumentationTabTypes]: ConfigurationItem[] } => {
  const objectTypeName = useSelector(selectSingleObject);
  const _fields = useSelector(selectParsedFields(crmOrgId))?.[objectTypeName?.objectType ?? ''];
  const rollups = useSelector(selectRollupsForTable);
  const users = useSelector(selectAccountUsers);

  const { parsedRules, parsedRecordTypes } = useDocumentation();
  const _parsedRecordTypes = useMemo(
    () =>
      parsedRecordTypes?.filter(
        (rt) => rt.objectApiName === objectTypeName?.objectType && rt.name !== '__MASTER__',
      ) ?? [],
    [parsedRecordTypes, objectTypeName],
  );

  const deployedVersions = useGetDeployedVersions(crmOrgId);
  const relatedObjects = useGetRelatedObjects(crmOrgId, objectTypeName?.objectType ?? '');

  const rulesByObjectName = useMemo(
    () => parsedRules.filter(ruleByObjectName(objectTypeName?.objectType ?? '')),
    [parsedRules, objectTypeName],
  );

  const objectConfigurationItems = useMemo(
    () =>
      rulesByObjectName
        .map((rule) => ({
          ...rule,
          parentType: rule.type,
        }))
        .map(createConfigurationItemFromParsedRule),
    [rulesByObjectName],
  );

  const groupedByType = useMemo(
    () => groupBy(objectConfigurationItems, 'type'),
    [objectConfigurationItems],
  );

  const {
    flows,
    approvalProcesses,
    processBuilderFlows,
    workflowRules,
    layouts,
    validationRules,
    apexTriggers,
    apexClasses,
    cpqData,
  } = groupedByType;

  const deployedSweepAutomations = useMemo(
    () =>
      updateStructureWithParentElementType(
        ConfigurationType.sweepAutomation,
        objectTypeName?.objectType ?? '',
        deployedVersions.automations,
      ),
    [objectTypeName?.objectType, deployedVersions.automations],
  );

  const deployedSweepAlerts = useMemo(
    () =>
      updateStructureWithParentElementType(
        ConfigurationType.sweepAlert,
        objectTypeName?.objectType ?? '',
        deployedVersions.alerts,
      ),
    [objectTypeName?.objectType, deployedVersions.alerts],
  );

  const deployedAssignments = useMemo(
    () =>
      updateStructureWithParentElementType(
        ConfigurationType.sweepAssignment,
        objectTypeName?.objectType ?? '',
        deployedVersions.assignments,
      ),
    [objectTypeName?.objectType, deployedVersions.assignments],
  );

  const deployedScheduledAssignments = useMemo(
    () =>
      updateStructureWithParentElementType(
        ConfigurationType.sweepScheduledAssignment,
        objectTypeName?.objectType ?? '',
        deployedVersions.scheduledAssignments,
      ),
    [objectTypeName?.objectType, deployedVersions.scheduledAssignments],
  );

  const dedupeNewStructure = useMemo(
    () =>
      updateStructureWithParentElementType(
        ConfigurationType.sweepDedupe,
        objectTypeName?.objectType ?? '',
        deployedVersions.dedup,
      ),
    [objectTypeName?.objectType, deployedVersions.dedup],
  );

  const matchingNewStructure = useMemo(
    () =>
      updateStructureWithParentElementType(
        ConfigurationType.sweepMatching,
        objectTypeName?.objectType ?? '',
        deployedVersions.matching,
      ),
    [objectTypeName?.objectType, deployedVersions.matching],
  );

  const list = useMemo(() => {
    return {
      [DocumentationTabTypes.FIELDS]:
        _fields?.map((field) => createConfigurationItemFromField(field, rollups)) ?? [],
      [DocumentationTabTypes.RECORD_TYPES]: (_parsedRecordTypes ?? []).map(
        createConfigurationItemFromRecordType,
      ) as ConfigurationItem[],
      [DocumentationTabTypes.SF_AUTOMATIONS]: [
        ...(flows ?? []),
        ...(approvalProcesses ?? []),
        ...(workflowRules ?? []),
        ...(processBuilderFlows ?? []),
      ],
      [DocumentationTabTypes.LAYOUTS]: layouts,
      [DocumentationTabTypes.VALIDATION_RULES]: validationRules ?? [],
      [DocumentationTabTypes.APEX]: [...(apexClasses ?? []), ...(apexTriggers ?? [])],
      [DocumentationTabTypes.CPQ_DATA]: cpqData,
      [DocumentationTabTypes.MATCHING_DEDUPE]: [...matchingNewStructure, ...dedupeNewStructure].map(
        (automation) => createConfigurationItemFromAutomation(automation, users),
      ) as ConfigurationItem[],
      [DocumentationTabTypes.PLAYBOOK_ALERTS]: deployedSweepAlerts.map((automation) =>
        createConfigurationItemFromAutomation(automation, users),
      ) as ConfigurationItem[],
      [DocumentationTabTypes.ASSIGNMENTS]: deployedAssignments.map((automation) =>
        createConfigurationItemFromAutomation(automation, users),
      ) as ConfigurationItem[],
      [DocumentationTabTypes.SCHEDULED_ASSIGNMENTS]: deployedScheduledAssignments.map(
        (automation) => createConfigurationItemFromAutomation(automation, users),
      ) as ConfigurationItem[],
      [DocumentationTabTypes.SWEEP_AUTOMATIONS]: deployedSweepAutomations.map((automation) =>
        createConfigurationItemFromAutomation(automation, users),
      ) as ConfigurationItem[],
      [DocumentationTabTypes.CARDS_LIST]: [],
      [DocumentationTabTypes.RELATED_OBJECTS]: relatedObjects,
      [DocumentationTabTypes.OTHER]: [],
    };
  }, [
    rollups,
    _fields,
    _parsedRecordTypes,
    processBuilderFlows,
    workflowRules,
    flows,
    approvalProcesses,
    layouts,
    validationRules,
    apexTriggers,
    cpqData,
    dedupeNewStructure,
    matchingNewStructure,
    deployedAssignments,
    deployedScheduledAssignments,
    deployedSweepAutomations,
    deployedSweepAlerts,
    users,
    relatedObjects,
    apexClasses,
  ]);

  return list;
};
